import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { DrawerReference, SynopticBayPlanItem, SynopticService } from '@connect-services/client/sdk/api';
import { InputNumberComponent } from '@connect-services/src/lib/components/input/input-number/input-number.component';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

export type CreateDrawerModalData = {
    depositId: string;
};

export type CreateDrawerModalResult = {
    drawer: DrawerReference;
    position: number;
    side: SynopticBayPlanItem.CoteTiroirEnum;
};

export class CreateDrawerForm extends FormGroup<{
    drawer: FormControl<DrawerReference | null>;
    position: FormControl<number>;
    side: FormControl<SynopticBayPlanItem.CoteTiroirEnum>;
}> {}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ComplexModalTemplate,
        InputNumberComponent,
        LoadingComponent,
        NoDataToDisplayComponent,
        RessourcesModule,
        SelectComponent,
    ],
    selector: 'app-create-drawer-modal',
    templateUrl: './create-drawer-modal.component.html',
})
export class CreateDrawerModalComponent implements OnInit {
    protected createDrawerForm!: CreateDrawerForm;
    protected data: CreateDrawerModalData = inject(MAT_DIALOG_DATA);
    protected drawerOptions: SelectOption<DrawerReference>[] = [];
    protected readonly isLoading = signal<boolean>(true);
    protected sideOptions: SelectOption<SynopticBayPlanItem.CoteTiroirEnum>[] = [];

    private dialogRef = inject(MatDialogRef<CreateDrawerModalComponent>);
    private readonly synopticService = inject(SynopticService);
    private readonly translocoService = inject(TranslocoService);

    public ngOnInit(): void {
        this.createDrawerForm = this.getForm();
        this.sideOptions = [
            {
                label: this.translocoService.translate('left').toUpperCase(),
                value: SynopticBayPlanItem.CoteTiroirEnum.GAUCHE,
            },
            {
                label: this.translocoService.translate('right').toUpperCase(),
                value: SynopticBayPlanItem.CoteTiroirEnum.DROITE,
            },
        ];
        void this.setDrawersReferences();
    }

    protected submit(modalResult: ModalResultEnum): void {
        if (this.createDrawerForm.invalid) {
            return;
        }

        if (modalResult === ModalResultEnum.CANCEL) {
            this.dialogRef.close(null);

            return;
        }

        this.dialogRef.close(this.createDrawerForm.getRawValue());
    }

    private getForm(): CreateDrawerForm {
        return new CreateDrawerForm({
            drawer: new FormControl<DrawerReference | null>(null, { validators: [Validators.required] }),
            position: new FormControl<number>(0, {
                nonNullable: true,
                validators: [Validators.required, Validators.min(1)],
            }),
            side: new FormControl<SynopticBayPlanItem.CoteTiroirEnum>(SynopticBayPlanItem.CoteTiroirEnum.GAUCHE, {
                nonNullable: true,
                validators: [Validators.required],
            }),
        });
    }

    private async setDrawersReferences(): Promise<void> {
        try {
            const references = await firstValueFrom(
                this.synopticService.synopticControllerGetDrawersReferences({
                    depositId: this.data.depositId,
                }),
            );

            this.drawerOptions = references.map((reference: DrawerReference) => {
                return {
                    label: reference.referenceName,
                    value: reference,
                };
            });
        } catch {
            this.drawerOptions = [];
        } finally {
            this.isLoading.set(false);
        }
    }
}
